import { type FC, useRef } from "react";
import { connect, useDispatch } from "react-redux";

import { ActionButtonIntent, DialogComponent, Popover, TriggerButton, type TriggerButtonProps } from "widgets";
import { useOverlay } from "widgets/hooks";

import style from "@/modules/files/styles/upload.module.scss";

import { type Dictionary } from "@/app-utils/type.ts";
import { FileUploadTile } from "@/modules/files/components/FileUploadTile.tsx";
import {
  type ICollectorResourceUploadData,
  type ICollectorResourceUploadState,
  cancelUploadRequest,
  clearUploadedCollectorResource,
} from "@/redux/modules/files.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";
import { FILE_STATUS } from "@/settings/settings.ts";

/* --- Redux --- */
function mapStateToProps(state: IAppState) {
  return {
    uploadResources: state.files.resourcesUploadLookup,
  };
}

function mapDispatchToProps(dispatch: ReduxDispatch) {
  return {
    clearFile: (resourceUploadData: ICollectorResourceUploadData) =>
      dispatch(clearUploadedCollectorResource(resourceUploadData)),
    cancelUploadRequest: (resourceUploadData: ICollectorResourceUploadData) =>
      dispatch(cancelUploadRequest(resourceUploadData)),
  };
}

/* --- Component --- */
// => Button + Pop-over
interface IUploadStateButtonOwnProps extends Pick<TriggerButtonProps, "isDisabled" | "tooltip"> {
  handleClickUploadButton: () => void;
}

interface IUploadStateButtonProps
  extends IUploadStateButtonOwnProps,
    ReturnType<typeof mapStateToProps>,
    ReturnType<typeof mapDispatchToProps> {
  showLabel?: boolean;
}

const UploadStateButton: FC<IUploadStateButtonProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { overlayProps, state, triggerProps } = useOverlay({});

  const allUploads: ICollectorResourceUploadState[] = Object.keys(props.uploadResources).map(
    (fileName: string) => props.uploadResources[fileName],
  );
  const pendingUploads = allUploads.filter((file) => file.uploadStatus === FILE_STATUS.UPLOAD_PENDING);
  const doneUploads = allUploads.filter((file) => file.uploadStatus !== FILE_STATUS.UPLOAD_PENDING);

  function clearAll() {
    doneUploads.forEach((file) => props.clearFile(file.file));
  }

  function cancelAll() {
    pendingUploads.forEach((file) => dispatch(cancelUploadRequest(file.file)));
  }

  if (!allUploads.length) {
    return (
      <TriggerButton
        icon={"cloud_upload"}
        onPress={() => props.handleClickUploadButton()}
        tooltip={props.tooltip}
        isDisabled={props.isDisabled}
        label={"Upload"}
        showLabel={props.showLabel ?? true}
      />
    );
  } else {
    return (
      <>
        <TriggerButton
          icon={pendingUploads.length ? "cloud_sync" : "cloud_done"}
          isDisabled={props.isDisabled}
          tooltip={props.tooltip}
          label={"Upload List"}
          badge={pendingUploads.length ? pendingUploads.length : true}
          showLabel={props.showLabel ?? true}
          {...triggerProps}
          ref={triggerRef}
        />
        <Popover state={state} triggerRef={triggerRef} {...overlayProps}>
          <DialogComponent
            title={"Upload"}
            closeFn={state.close}
            actions={[
              <TriggerButton
                icon={"cloud_upload"}
                onPress={() => {
                  state.close();
                  props.handleClickUploadButton();
                }}
                isDisabled={props.isDisabled}
                tooltip={props.tooltip}
                key={"upload-state-button-action"}
                label={"Upload"}
              />,
            ]}
            mainButton={{
              label: "Clear List",
              onPress: () => clearAll(),
              isDisabled: doneUploads.length === 0,
              intent: ActionButtonIntent.Primary,
            }}
            secondaryButton={
              pendingUploads.length === 0
                ? undefined
                : {
                    label: "Stop All Uploads",
                    onPress: () => cancelAll(),
                    intent: ActionButtonIntent.Danger,
                  }
            }
          >
            <List
              uploadResources={props.uploadResources}
              clearFile={props.clearFile}
              cancelUploadRequest={props.cancelUploadRequest}
            />
          </DialogComponent>
        </Popover>
      </>
    );
  }
};

// => List of files
function List({
  uploadResources,
  clearFile,
  cancelUploadRequest,
}: {
  uploadResources: Dictionary<ICollectorResourceUploadState>;
  clearFile: (resourceUploadData: ICollectorResourceUploadData) => void;
  cancelUploadRequest: (resourceUploadData: ICollectorResourceUploadData) => void;
}) {
  const allUploads: ICollectorResourceUploadState[] = Object.keys(uploadResources).map(
    (fileName: string) => uploadResources[fileName],
  );

  // sort list by status (completed first) and by time (older first)
  allUploads
    .sort((a, b) => a.uploadStarted - b.uploadStarted)
    .sort((a, _b) => (a.uploadStatus === FILE_STATUS.UPLOAD_COMPLETE ? -1 : 1));

  return (
    <div className={style.list}>
      {allUploads.map((upload, index) => (
        <FileUploadTile
          clearFile={clearFile}
          cancelUploadRequest={cancelUploadRequest}
          file={upload.file}
          uploadStatus={upload.uploadStatus}
          progress={upload.progress}
          errors={upload.errors}
          key={index}
        />
      ))}
    </div>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(UploadStateButton);
